import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import Dialog from "components/NoScrollDialog";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import loadingQrImage from "assets/images/loading-qr.jpg";
import AlertDialog from "components/AlertDialog";
import LoadingButton from "components/LoadingButton";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuth } from "contexts/auth.context";
import { useLanguage } from "contexts/language.context";
import { useEffect, useState } from "react";
import OTPInput from "react-otp-input";
import profileService from "services/profile-service";
import usersService from "services/users-service";
import notificationsService from "services/notifications-service";

function getTemplateType(template) {
  if (template.type === 1 || template.type === "EMAIL" || template.type === "email") {
    return "email";
  }
  if (
    template.type === 2 ||
    template.type === "NOTIFICATION" ||
    template.type === "notification"
  ) {
    return "notification";
  }

  const description = String(template.description || "").toLowerCase();
  if (description.startsWith("email")) return "email";
  if (description.startsWith("notify")) return "notification";
  return "other";
}

function SettingToggleRow({ label, checked, onChange }) {
  return (
    <MDBox
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        py: 0.5,
      }}
    >
      <MDTypography
        variant="button"
        fontWeight="regular"
        color="text"
        sx={{ flex: 1, lineHeight: 1.5 }}
      >
        {label}
      </MDTypography>
      <Switch checked={checked} onChange={onChange} />
    </MDBox>
  );
}

function getTemplateLabelKey(template) {
  const type = getTemplateType(template);
  const entity = String(template.entity || "").toLowerCase();
  const action = String(template.action || "").toLowerCase();

  if (!type || !entity || !action || type === "other") {
    return null;
  }

  return `${type}-${entity}-${action}-label`;
}

function PlatformSettings({ profile }) {
  const { label } = useLanguage();
  const auth = useAuth();
  const user = auth.user;

  const [setupDialog, setSetupDialog] = useState(false);
  const [alertIsOpen, setAlertIsOpen] = useState(false);

  const [notificationTemplates, setNotificationTemplates] = useState([]);
  const [userNotificationTemplates, setUserNotificationTemplates] = useState(
    auth.user.userNotificationTemplates,
  );

  useEffect(() => {
    setUserNotificationTemplates(auth.user?.userNotificationTemplates || []);
  }, [auth.user]);

  useEffect(() => {
    getNotificationTemplates();
  }, []);

  const auditNotificationTemplates = notificationTemplates.filter(
    (template) => template.entity === "audit",
  );
  const taskNotificationTemplates = notificationTemplates.filter(
    (template) => template.entity === "task",
  );
  const emailAuditTemplates = auditNotificationTemplates.filter(
    (template) => getTemplateType(template) === "email",
  );
  const emailTaskTemplates = taskNotificationTemplates.filter(
    (template) => getTemplateType(template) === "email",
  );
  const pushAuditTemplates = auditNotificationTemplates.filter(
    (template) => getTemplateType(template) === "notification",
  );
  const pushTaskTemplates = taskNotificationTemplates.filter(
    (template) => getTemplateType(template) === "notification",
  );

  async function getNotificationTemplates() {
    const [response, data] =
      await notificationsService.getNotificationTemplates();
    if (response.ok) {
      setNotificationTemplates(data);
    }
  }

  async function handleUpdateProfile(newUserNotificationTemplates) {
    const [response, data] = await profileService.updateUser(profile.id, {
      userNotificationTemplates:
        newUserNotificationTemplates || userNotificationTemplates,
    });
    if (response.ok) {
      await auth.getUser();
      return true;
    } else {
      console.log("ERROR while updating profile: " + data.error);
      return false;
    }
  }

  async function disableTFA() {
    const [response, data] = await usersService.disableTFA();
    if (response.ok) {
      await auth.getUser();
    } else {
      console.log("ERROR while updating profile: " + data.error);
    }
  }

  function renderTemplateToggles(templates) {
    return templates.map((notificationTemplate) => (
      <SettingToggleRow
        key={notificationTemplate.id}
        label={
          label(getTemplateLabelKey(notificationTemplate)) ||
          notificationTemplate.description
        }
        checked={!!(
          userNotificationTemplates.find(
            (v) => v.notificationTemplateId == notificationTemplate.id,
          )?.enabled
        )}
        onChange={(event) => {
          const checked = event.target.checked;
          const previousUserNotificationTemplates = userNotificationTemplates;
          const existingTemplateIndex = userNotificationTemplates.findIndex(
            (v) => v.notificationTemplateId == notificationTemplate.id,
          );
          const newUserNotificationTemplates =
            existingTemplateIndex === -1
              ? [
                  ...userNotificationTemplates,
                  {
                    notificationTemplateId: notificationTemplate.id,
                    enabled: checked,
                  },
                ]
              : userNotificationTemplates.map((template) =>
                  template.notificationTemplateId ==
                  notificationTemplate.id
                    ? {
                        ...template,
                        enabled: checked,
                      }
                    : template,
                );

          setUserNotificationTemplates(newUserNotificationTemplates);

          handleUpdateProfile(newUserNotificationTemplates).then((ok) => {
            if (!ok) {
              setUserNotificationTemplates(previousUserNotificationTemplates);
            }
          });
        }}
      />
    ));
  }

  function renderTemplateGroup(title, templates) {
    if (!templates.length) return null;

    return (
      <MDBox>
        <MDTypography
          variant="caption"
          fontWeight="bold"
          color="text"
          textTransform="uppercase"
          sx={{ display: "block", mb: 1 }}
        >
          {title}
        </MDTypography>
        <MDBox display="flex" flexDirection="column" gap={1}>
          {renderTemplateToggles(templates)}
        </MDBox>
      </MDBox>
    );
  }

  return (
    <Card sx={{ boxShadow: "none", width: "100%" }}>
      <AlertDialog
        open={alertIsOpen}
        setOpen={setAlertIsOpen}
        title={label("tfa-disable-alert-title")}
        okContent={label("disable-button")}
        danger
        content={label("tfa-disable-alert-message")}
        onOk={async () => {
          await disableTFA();
        }}
      />
      <MDBox p={2} sx={{ width: "100%" }}>
        <MDTypography
          variant="h6"
          fontWeight="medium"
          textTransform="capitalize"
        >
          {label("platform-settings-label")}
        </MDTypography>
      </MDBox>
      <Grid container spacing={2} sx={{ width: "100%" }}>
        <Grid xs={12} md={6} item>
          <MDBox pt={1} pb={2} px={2} lineHeight={1.25} display="flex" flexDirection="column" gap={2}>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              color="text"
              textTransform="uppercase"
              sx={{ display: "block", mb: 1.5 }}
            >
              Email
            </MDTypography>
            {renderTemplateGroup("Audit", emailAuditTemplates)}
            {renderTemplateGroup("Tasks", emailTaskTemplates)}
        </MDBox>
        </Grid>
        <Grid xs={12} md={6} item>
          <MDBox pt={1} pb={2} px={2} lineHeight={1.25} display="flex" flexDirection="column" gap={2}>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              color="text"
              textTransform="uppercase"
              sx={{ display: "block", mb: 1.5 }}
            >
              Notifications
            </MDTypography>
            {renderTemplateGroup("Audit", pushAuditTemplates)}
            {renderTemplateGroup("Tasks", pushTaskTemplates)}
          </MDBox>
        </Grid>
      </Grid>
      <MDBox pt={1} pb={2} px={2} lineHeight={1.25}>
        <MDTypography
          variant="caption"
          fontWeight="bold"
          color="text"
          textTransform="uppercase"
          sx={{ display: "block", mb: 1.5 }}
        >
          {label("two-factor-authentication")}
        </MDTypography>
        <SettingToggleRow
          label={label("enable-tfa-label")}
          checked={!!(user.isEnabledTFA && user.isVerifiedTFA)}
          onChange={(event) => {
            const checked = event.target.checked;

            if (checked) {
              setSetupDialog(true);
            } else {
              setAlertIsOpen(true);
            }
          }}
        />

        <MDTypography
          fontWeight="regular"
          color="text"
          variant="caption"
          sx={{ display: "block", mt: 1.5 }}
        >
          {label("tfa-info")}
        </MDTypography>

        <SetupOTPDialog open={setupDialog} setOpen={setSetupDialog} />
      </MDBox>
    </Card>
  );
}

export function SetupOTPDialog({ open, setOpen }) {
  const auth = useAuth();
  const { label } = useLanguage();
  const [qr, setQr] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setOtp("");
    setError("");
    setQr("");

    if (open) {
      generateQrCode();
    }
  }, [open]);

  async function handleVerifyUserTFA(otp) {
    const [response, data] = await usersService.verifyUserTFA({
      token: otp,
    });
    if (response.ok) {
      await auth.getUser();
      return true;
    } else {
      console.log("ERROR while updating tfa: " + data.error);
      return false;
    }
  }

  async function generateQrCode() {
    setLoading(true);
    const [response, data] = await usersService.generateQrCodeTFA();

    if (response.ok) {
      setQr(data.url);
    }
    setLoading(false);
  }

  return (
    <Dialog
      open={open}
      component={"form"}
      onSubmit={async (e) => {
        e.preventDefault();
        if (otp.trim() === "") {
          setError(true);
          return;
        }
        setLoading(true);
        const ok = await handleVerifyUserTFA(otp);
        setLoading(false);
        if (ok) {
          setOpen(false);
        } else {
          setError("Invalid OTP");
        }
      }}
    >
      <DialogTitle>{label("enable-tfa-title")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ fontWeight: "400" }}>
          {label("enable-tfa-details")}
        </Typography>
        <MDBox
          width="100%"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={qr || loadingQrImage}
            alt="QR Code for 2FA"
            style={{ width: "180px", height: "180px" }}
          />
        </MDBox>
        <MDBox width="100%" sx={{ pt: 2 }}>
          <OTPInput
            shouldAutoFocus
            value={otp}
            numInputs={6}
            onChange={setOtp}
            inputStyle={{
              flex: 1,
              minHeight: "80px",
              textAlign: "center",
              fontSize: "32px",
              borderRadius: "8px",
              border: "1px solid rgba(0, 0, 0, 0.15)",
              fontFamily: "Roboto",
            }}
            renderSeparator={<Box sx={{ width: "10px" }} />}
            renderInput={(props) => <input {...props} />}
          />
          {error && (
            <Grid item xs={12}>
              <MDTypography variant="button" fontWeight="regular" color="error">
                {label("invalid-otp")}
              </MDTypography>
            </Grid>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton
          onClick={() => {
            setOpen(false);
          }}
        >
          {label("cancel-button")}
        </MDButton>
        <LoadingButton
          disabled={otp?.length < 6}
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
        >
          {label("verify-otp-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default PlatformSettings;
